import type { Message } from "@ag-ui/client";
import { isHitlTool } from "./hitlTools";

// The turn's new messages — the only ones the server needs on the wire. The
// database is the source of truth for history: the server rehydrates prior
// turns from it and appends what arrives here.
//
// A turn contributes exactly one of two shapes:
//   - a new user message (ordinary chat), or
//   - the answers to the paused human-in-the-loop calls, which assistant-ui
//     stages as tool results before it resumes the run.
//
// The answers are picked by tool name rather than by position. assistant-ui
// accumulates an entire run into a single assistant message, so `toAgUiMessages`
// re-emits every tool result the run produced immediately before the answer.
// Those calls are already closed in the database, and the server refuses an
// answer for a call it does not show pending — only a pausing tool's result is
// ever authored here.
export function newOutboundMessages(messages: readonly Message[]): Message[] {
  const last = messages.at(-1);
  if (!last) return [];
  if (last.role === "user") return [last];
  if (last.role !== "tool") return [];

  const hitlCallIds = new Set<string>();
  for (const message of messages) {
    if (message.role !== "assistant") continue;
    for (const call of message.toolCalls ?? []) {
      if (isHitlTool(call.function.name)) hitlCallIds.add(call.id);
    }
  }

  let start = messages.length;
  while (start > 0 && messages[start - 1].role === "tool") start--;
  return messages
    .slice(start)
    .filter((message) => message.role === "tool" && hitlCallIds.has(message.toolCallId));
}
